import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useOutletContext, useParams } from "react-router";
import { Plus } from "lucide-react";
import { NewContactDefaultsPanel } from "@/components/journey/NewContactDefaultsPanel";
import {
  AdminEmptyState,
  AdminList,
  AdminListRow,
  AdminPageHeader,
  AdminPill,
  AdminSectionCard,
} from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  JourneyApiError,
  listJourneys,
  type JourneyDefinition,
} from "@/lib/journey-api";
import type { JourneyOutletContext } from "@/pages/journey/JourneyLayout";

function statusLabel(status: string) {
  if (status === "enabled") return "已启用";
  if (status === "draft") return "草稿";
  return status;
}

/**
 * 旅程设置 · 旅程配置：旅程列表 + 默认旅程（原列表页 + Settings 配置区合并）
 */
export default function JourneyConfigPage() {
  const { brandId } = useParams();
  const location = useLocation();
  const { accountId } = useAccountWorkspace();
  const { brandName } = useOutletContext<JourneyOutletContext>();
  const [journeys, setJourneys] = useState<JourneyDefinition[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const base = `/accounts/${accountId}/brands/${brandId}/apps/journey`;

  const load = useCallback(async () => {
    if (!brandId) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = await listJourneys(accountId, brandId);
      setJourneys(payload.journeys);
    } catch (caught) {
      setError(caught instanceof JourneyApiError ? caught.message : "无法加载旅程列表");
      setJourneys([]);
    } finally {
      setIsLoading(false);
    }
  }, [accountId, brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  // 从客户页或其它深链滚到「新客户默认」
  useEffect(() => {
    if (location.hash !== "#new-contact-defaults") return;
    const el = document.getElementById("new-contact-defaults");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="旅程配置"
        description={
          <>
            {brandName ? `「${brandName}」的旅程列表与默认策略。` : null}
            需在企业微信确认后才会发给客户。
          </>
        }
        actions={
          <Button asChild size="sm">
            <Link to={`${base}/journeys/new`}>
              <Plus data-icon="inline-start" />
              新建旅程
            </Link>
          </Button>
        }
      />

      {isLoading ? <p className="text-sm text-muted-foreground">正在加载旅程…</p> : null}

      {error ? (
        <p
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {!isLoading && !error && journeys && journeys.length === 0 ? (
        <AdminEmptyState>
          <p className="text-base font-medium text-foreground">还没有旅程</p>
          <p className="mt-1">到点后会提醒员工在企微确认发送</p>
          <Button asChild className="mt-4" size="sm">
            <Link to={`${base}/journeys/new`}>新建旅程</Link>
          </Button>
        </AdminEmptyState>
      ) : null}

      {!isLoading && journeys && journeys.length > 0 ? (
        <AdminList>
          {journeys.map((journey) => (
            <AdminListRow key={journey.id}>
              <div className="min-w-0 space-y-1">
                <Link
                  to={`${base}/journeys/${journey.id}`}
                  className="font-medium text-foreground hover:underline"
                >
                  {journey.name}
                </Link>
                <p className="text-xs text-muted-foreground">
                  {journey.nodes.length} 个节点 · 到点后需在企微确认发送
                </p>
              </div>
              <AdminPill tone={journey.status === "enabled" ? "success" : "muted"}>
                {statusLabel(journey.status)}
              </AdminPill>
            </AdminListRow>
          ))}
        </AdminList>
      ) : null}

      <AdminSectionCard
        id="new-contact-defaults"
        title="新客户默认"
        description="同步进来的新客户默认绑定哪条旅程。按本品牌保存。"
      >
        {accountId && brandId ? (
          <NewContactDefaultsPanel
            accountId={accountId}
            brandId={brandId}
            idPrefix="settings-new-contact"
          />
        ) : null}
      </AdminSectionCard>

      <AdminSectionCard title="渠道" description="启用旅程前须完成账户级企微接入就绪检查。">
        <Link
          to={`/accounts/${accountId}/channels`}
          className="text-sm font-medium text-primary-readable hover:underline"
        >
          打开渠道总览 →
        </Link>
      </AdminSectionCard>
    </div>
  );
}
